import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import AppImage from "../AppImage";
import Button from "./Button";

// CSS for sliding animation
const slidingStyle = `
  @keyframes slideRightToLeft {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(-100%);
    }
  }
  @keyframes navbarPulse {
    0%,
    100% {
      background: linear-gradient(135deg, rgba(37, 99, 235, 0.95), rgba(59, 130, 246, 0.85));
      color: #ffffff;
      box-shadow: 0 0 0 rgba(250, 204, 21, 0), 0 0 0 rgba(59, 130, 246, 0);
      border-color: rgba(250, 204, 21, 0.6);
    }
    50% {
      background: linear-gradient(135deg, rgba(250, 204, 21, 0.95), rgba(96, 165, 250, 0.9), rgba(254, 240, 138, 0.9));
      color: #0f172a;
      box-shadow: 0 0 12px rgba(250, 204, 21, 0.8), 0 0 18px rgba(59, 130, 246, 0.5);
      border-color: rgba(255, 255, 255, 0.8);
    }
  }
  .sliding-text {
    animation: slideRightToLeft 80s linear infinite;
    white-space: nowrap;
  }
  .navbar-highlight {
    animation: navbarPulse 1.2s ease-in-out infinite;
    border: 1px solid rgba(250, 204, 21, 0.7);
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigationItems = [
    { name: "Home", path: "/homepage", icon: "Home" },
    { name: "About", path: "/about", icon: "Info" },
    { name: "Departments", path: "/departments", icon: "Building2" },
    { name: "Courses", path: "/courses", icon: "BookOpen" },
    { name: "Events", path: "/graduation", icon: "GraduationCap" },

    {
      name: "Resources",
      path: "/resources",
      icon: "FileText",
      dropdown: [
        {
          name: "Documents",
          path: "/resources?tab=documents",
          icon: "FileText",
        },
        {
          name: "Service Charter",
          path: "/resources?tab=service-charter",
          icon: "FileCheck",
        },
        {
          name: "Gallery",
          path: "/resources?tab=gallery",
          icon: "GalleryThumbnails",
        },
        {
          name: "Open Tenders",
          path: "/resources?tab=tenders",
          icon: "Briefcase",
        },
        { name: "Career", path: "/resources?tab=careers", icon: "Briefcase" },
        {
          name: "Academic Calendar",
          path: "/resources?tab=calendar",
          icon: "Calendar",
        },
      ],
    },
    {
      name: "Portal",
      path: "https://karumottiportal.ac.ke",
      icon: "Link",
      external: true,
    },
    {
      name: "Audit",
      path: "https://karumotti.dimensionplus.co.ke/",
      icon: "Link",
      external: true,
    },
  ];

  const secondaryItems = [{ name: "Contact", path: "/contact", icon: "Phone" }];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleDropdown = (itemName) => {
    setDropdownOpen(dropdownOpen === itemName ? null : itemName);
  };

  const closeDropdown = () => {
    setDropdownOpen(null);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    if (confirm("Are you sure you want to logout?")) {
      sessionStorage.removeItem("adminToken");
      navigate("/workshop");
    }
  };

  return (
    <>
      <style>{slidingStyle}</style>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-blue-400 lg:bg-blue-400 backdrop-blur-md shadow-card border-b border-border"
            : "bg-blue-400 lg:bg-blue-400"
        }`}
      >
        {/* Sliding Contact Line */}
        <div
          className="bg-gradient-f text-black py-2 overflow-hidden"
          style={{ backgroundColor: "#ffff00" }}
        >
          <div className="sliding-text text-sm font-medium">
            📞 Phone: +254 (0) 705636443 | ✉️ Email: info@karumotti.ac.ke | 📍
            Address: P. O. Box 276 - 60602 Kianjai, Meru, Kenya | 📞 Phone: +254
            (0) 705636443 | ✉️ Email: info@karumotti.ac.ke | 📍 Address: P. O.
            Box 276 - 60602 Kianjai, Meru, Kenya
          </div>
        </div>
        <div className="w-full">
          <div className="flex items-center justify-between h-20 px-6 lg:px-8">
            {/* Logo Section */}
            <Link
              to="/homepage"
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
            >
              <div className="relative">
                <AppImage
                  src="/kttilogo.png"
                  alt="KTTI Logo"
                  className="w-25 h-20 rounded-lg"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-2xl font-bold text-white">
                  KARUMO TECHNICAL TRAINING INSTITUTE
                </h1>
                <p className="text-l -mt-1" style={{ color: "#ffff00" }}>
                  Where Creativity and Innovation Deliver Solution.
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems?.map((item) =>
                item.external ? (
                  <a
                    key={item?.path}
                    href={item?.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`relative px-4 py-2 rounded-lg text-l font-black transition-all duration-200 flex items-center space-x-2 text-white hover:text-white hover:bg-yellow-200`}
                  >
                    <Icon name={item?.icon} size={16} />
                    <span>{item?.name}</span>
                  </a>
                ) : item.dropdown ? (
                  <div key={item?.path} className="relative">
                    <button
                      onClick={() => toggleDropdown(item?.name)}
                      className={`relative px-4 py-2 rounded-lg text-l font-black transition-all duration-200 flex items-center space-x-2 ${
                        isActivePath(item?.path)
                          ? "text-white bg-blue-400"
                          : "text-white hover:text-black hover:bg-yellow-200"
                      }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.name}</span>
                      <Icon
                        name="ChevronDown"
                        size={14}
                        className={`ml-1 transition-transform ${
                          dropdownOpen === item?.name ? "rotate-180" : ""
                        }`}
                      />
                      {isActivePath(item?.path) && (
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></div>
                      )}
                    </button>
                    {dropdownOpen === item?.name && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                        {item?.dropdown?.map((subItem) => (
                          <Link
                            key={subItem?.path}
                            to={subItem?.path}
                            onClick={closeDropdown}
                            className="flex items-center space-x-2 px-4 py-3 text-lg text-gray-900 hover:bg-gray-100 transition-colors duration-200"
                          >
                            <Icon name={subItem?.icon} size={16} />
                            <span>{subItem?.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`relative px-4 py-2 rounded-lg text-l font-black transition-all duration-200 flex items-center space-x-2 ${
                      isActivePath(item?.path)
                        ? "text-white bg-blue-400"
                        : "text-white hover:text-black hover:bg-yellow-200"
                    } ${item?.highlight ? "navbar-highlight" : ""}`}
                  >
                    <Icon name={item?.icon} size={16} />
                    <span>{item?.name}</span>
                    {isActivePath(item?.path) && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></div>
                    )}
                  </Link>
                ),
              )}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              {location.pathname === "/workshop" &&
              sessionStorage.getItem("adminToken") ? (
                <Button
                  variant="outline"
                  size="xl"
                  iconName="LogOut"
                  iconPosition="left"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              ) : (
                <Link to="/contact">
                  <Button
                    variant=""
                    size="xl"
                    iconName="Phone"
                    iconPosition="left"
                  >
                    Contact
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden p-2 rounded-lg hover:bg-yellow-400 transition-colors duration-200"
              aria-label="Toggle menu"
            >
              <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>

          {/* Mobile Navigation */}
          <div
            className={`lg:hidden transition-all duration-300 ease-in-out ${
              isMenuOpen
                ? "max-h-96 opacity-100"
                : "max-h-0 opacity-0 overflow-hidden"
            }`}
          >
            <div className="px-6 py-4 bg-yellow-400 border-t border-border">
              <nav className="space-y-2">
                {[...navigationItems, ...secondaryItems]?.map((item) =>
                  item.external ? (
                    <a
                      key={item?.path}
                      href={item?.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-black hover:text-black hover:bg-blue-400"
                    >
                      <Icon name={item?.icon} size={18} />
                      <span>{item?.name}</span>
                    </a>
                  ) : item.dropdown ? (
                    <div key={item?.path}>
                      <button
                        onClick={() => toggleDropdown(item?.name)}
                        className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActivePath(item?.path)
                            ? "text-black bg-blue-400"
                            : "text-black hover:text-black hover:bg-blue-400"
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon name={item?.icon} size={18} />
                          <span>{item?.name}</span>
                        </div>
                        <Icon
                          name="ChevronDown"
                          size={14}
                          className={`transition-transform ${
                            dropdownOpen === item?.name ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {dropdownOpen === item?.name && (
                        <div className="ml-6 mt-2 space-y-1">
                          {item?.dropdown?.map((subItem) => (
                            <Link
                              key={subItem?.path}
                              to={subItem?.path}
                              onClick={() => {
                                setIsMenuOpen(false);
                                closeDropdown();
                              }}
                              className="flex items-center space-x-3 px-4 py-2 rounded-lg text-sm text-black hover:text-black hover:bg-blue-400 transition-all duration-200"
                            >
                              <Icon name={subItem?.icon} size={16} />
                              <span>{subItem?.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActivePath(item?.path)
                          ? "text-black bg-blue-400"
                          : "text-black hover:text-black hover:bg-blue-400"
                      }`}
                    >
                      <Icon name={item?.icon} size={18} />
                      <span>{item?.name}</span>
                    </Link>
                  ),
                )}
                {location.pathname === "/workshop" &&
                  sessionStorage.getItem("adminToken") && (
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-black hover:text-black hover:bg-blue-400 w-full text-left"
                    >
                      <Icon name="LogOut" size={18} />
                      <span>Logout</span>
                    </button>
                  )}
              </nav>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
